import { Form, Field } from 'react-final-form';
import createDecorator from 'final-form-focus';

import * as Styles from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.styled';

import { BaseModal } from '../../../../../components/Toolkit/Modal/BaseModal';
import { Checkbox } from '../../../../../components/Toolkit/Inputs/Checkbox';
import { TextField } from '../../../../../components/Toolkit/Inputs/TextField';
import { TextArea } from '../../../../../components/Toolkit/Inputs/TextArea';
import { Button } from '../../../../../components/Toolkit/Button/Button';
import { TradeInManager } from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/components/TradeInManager';
import { ShowInterestOptions } from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/components/ShowInterestOptions';
import { Loading } from '../../../../../components/Toolkit/Loading/Loading';

import {
  required,
  isEmail,
  isPhoneNumber,
  composeValidators,
} from '../../../../../helpers/InputValidators';
import { handleErrorMessage } from '../../../../../helpers/forms';

import { MILEAGE_UNIT } from '../../../../../types';
import { FORM_VALUES } from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.config';
import type { IEnquiryData } from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.config';

import { useDealerEnquiryModalReducer } from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.reducer';
import { useDealerEnquiryModal } from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.hook';
import type { DealerEnquiryModalProps } from '../../../../../components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.typed';

import { CDN_STATIC_ASSETS } from '../../../../../utils';

const focusOnErrors = createDecorator<IEnquiryData>();

function DealerEnquiryModal(props: DealerEnquiryModalProps) {
  const {
    isOpen,
    dealerName,
    dealerLogo,
    enquiry = {
      contactName: '',
      contactNo: '',
      email: '',
      message: '',
      showInterestOptions: false,
      financingVehicle: false,
      schedulingTestDrive: false,
      vehicleTradeIn: false,
      vehicleCondition: false,
      saveEnquiry: false,
    },
    askToClose,
    adId,
    sectionName,
    authorizationHeader,
    setIsPostEnquirySimilarAdsModalOpen,
    setSimilarAds,
  } = props;

  const {
    contactName,
    contactNo,
    email,
    message,
    showInterestOptions,
    financingVehicle = false,
    schedulingTestDrive = false,
    vehicleTradeIn = false,
    vehicleCondition = false,
    saveEnquiry = false,
    registrationNo = '',
    mileage = '',
    mileageType = MILEAGE_UNIT.KILOMETRES,
  } = enquiry;

  const DEFAULT_MESSAGE =
    'Hi, I am interested in this ad. Can you please contact me about it?\n\nThanks';

  const {
    setShowKeyInfoList,
    isSubmitting,
    disableSubmitButton,
    showKeyInfoList,
    setTradeInValues,
    setDisableVehicleRegLookupButton,
    vehicleLookupErrorMessage,
    disableVehicleRegLookupButton,
    setVehicleLookUpErrorMessage,
    resetModal,
    setIsSubmitting,
    setDisableSubmitButton,
    tradeInValues,
    onLookupSuccess,
    onLookupFailure,
    populateModal,
  } = useDealerEnquiryModalReducer();

  const { handleDataFromVehicleLookup, handleClose, onSubmit, resultsRef } =
    useDealerEnquiryModal({
      enquiry,
      askToClose,
      includesDefaultMessageText: message === DEFAULT_MESSAGE,
      adId,
      authorizationHeader,
      sectionName,
      resetModal,
      setIsSubmitting,
      setDisableSubmitButton,
      tradeInValues,
      disableVehicleRegLookupButton,
      onLookupSuccess,
      onLookupFailure,
      populateModal,
      setIsPostEnquirySimilarAdsModalOpen,
      setSimilarAds,
    });

  return (
    <BaseModal
      isOpen={isOpen}
      askToClose={handleClose}
      testId="dealer-enquiry-modal"
      fixedButtonGroup
      variant="WIDE"
    >
      <Styles.Content>
        <Styles.Header
          illustration={dealerLogo}
          title={dealerName}
          listItems={['Send enquiry to dealer']}
          shouldTruncate
        />
        <Form
          onSubmit={onSubmit}
          decorators={[focusOnErrors]}
          initialValues={{
            message,
            contactName,
            contactNo,
            email,
            mileage,
            mileageType,
            financingVehicle,
            schedulingTestDrive,
            vehicleTradeIn,
            vehicleCondition,
            registrationNo,
            saveEnquiry,
          }}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Field
                name={FORM_VALUES.CONTACT_NAME}
                validate={required('Name Required')}
              >
                {({ input, meta }) => (
                  <TextField
                    {...input}
                    type="text"
                    errorMessage={handleErrorMessage(meta)}
                    label="Your full name"
                  />
                )}
              </Field>
              <Field
                name={FORM_VALUES.CONTACT_NO}
                validate={composeValidators(
                  required('Please enter a valid phone number'),
                  isPhoneNumber,
                )}
              >
                {({ input, meta }) => (
                  <TextField
                    {...input}
                    type="tel"
                    inputMode="tel"
                    label="Your number"
                    errorMessage={handleErrorMessage(meta)}
                  />
                )}
              </Field>
              <Field
                name={FORM_VALUES.EMAIL}
                validate={composeValidators(
                  required('Email Required'),
                  isEmail,
                )}
              >
                {({ input, meta }) => (
                  <TextField
                    {...input}
                    type="email"
                    errorMessage={handleErrorMessage(meta)}
                    label="Your email"
                    disabled
                  />
                )}
              </Field>
              {showInterestOptions && (
                <ShowInterestOptions setShowKeyInfoList={setShowKeyInfoList} />
              )}
              <Field
                name={FORM_VALUES.MESSAGE}
                validate={required('Message Required')}
              >
                {({ input, meta }) => (
                  <TextArea
                    {...input}
                    errorMessage={handleErrorMessage(meta)}
                    label="Your message"
                    placeholder={`e.g. Hi, I am interested in this ad. Can you please contact me about it? Thanks`}
                  />
                )}
              </Field>
              {showKeyInfoList && (
                <TradeInManager
                  setTradeInValues={setTradeInValues}
                  tradeInValues={tradeInValues}
                  setDisableVehicleRegLookupButton={
                    setDisableVehicleRegLookupButton
                  }
                  resultsRef={resultsRef}
                  onVehicleLookUpInputCallback={handleDataFromVehicleLookup}
                  errorMessage={vehicleLookupErrorMessage}
                  setErrorMessage={setVehicleLookUpErrorMessage}
                  disableVehicleRegLookupButton={disableVehicleRegLookupButton}
                />
              )}
              {showInterestOptions && (
                <Field name={FORM_VALUES.SAVE_ENQUIRY}>
                  {({ input }) => (
                    <Checkbox
                      {...input}
                      label="Save details for later"
                      checked={input.value}
                    />
                  )}
                </Field>
              )}
              <Styles.Disclaimer>
                Your personal details will only be passed to this dealer for
                them to contact you.
              </Styles.Disclaimer>
              <Styles.SModalButtonGroup>
                {!isSubmitting ? (
                  <>
                    <Button ofType="SECONDARY" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      data-testid="send"
                      disabled={disableSubmitButton}
                    >
                      Send
                    </Button>
                  </>
                ) : (
                  <Button>
                    <Loading color="LIGHT" cdnUrl={CDN_STATIC_ASSETS} />
                  </Button>
                )}
              </Styles.SModalButtonGroup>
            </form>
          )}
        />
      </Styles.Content>
    </BaseModal>
  );
}

export { DealerEnquiryModal };
